import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CreditCard, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, dataIt, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";

const STATI = { prova: "Prova gratuita", attivo: "Abbonamento attivo", attivo_gratuito: "Attivo (gratuito)", insoluto: "Pagamento non riuscito", scaduto: "Scaduto", sospeso: "Sospeso" };

const Piano = ({ id, sel, onSel, titolo, prezzo, per, nota, badge }) => (
  <button type="button" onClick={() => onSel(id)} data-testid={`piano-${id}`}
    className={`relative rounded-lg border-2 p-4 text-left transition-colors ${sel === id ? "border-orange-600 bg-orange-50" : "border-slate-200 hover:border-slate-300"}`}>
    {badge && <span className="absolute -top-2.5 right-3 rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">{badge}</span>}
    <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{titolo}</p>
    <p className="mt-1 font-heading text-2xl font-extrabold text-slate-900">{prezzo}<span className="text-sm font-medium text-slate-500"> {per}</span></p>
    <p className="mt-1 text-xs text-slate-600">{nota}</p>
  </button>
);

export default function Abbonamento() {
  const { user, ricarica } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [a, setA] = useState(null);
  const [busy, setBusy] = useState(false);
  const [verifica, setVerifica] = useState(false);
  const [piano, setPiano] = useState("annuale");
  const load = () => api.get("/abbonamento").then((r) => setA(r.data));

  useEffect(() => {
    load();
    const ppId = sp.get("subscription_id");
    if (sp.get("paypal") && ppId) {
      setVerifica(true);
      let k = 0;
      const pollPP = async () => {
        const { data } = await api.get("/abbonamento/paypal/conferma", { params: { subscription_id: ppId } }).catch(() => ({ data: {} }));
        if (data.attivo) {
          toast.success("Abbonamento PayPal attivato, grazie!");
          setVerifica(false); setSp({}, { replace: true }); load(); ricarica();
        } else if (++k < 15) setTimeout(pollPP, 2000);
        else { setVerifica(false); toast.error("Pagamento PayPal non ancora confermato: ricarica la pagina tra poco"); }
      };
      pollPP();
      return;
    }
    const sid = sp.get("session_id");
    if (!sid) return;
    setVerifica(true);
    let n = 0;
    const poll = async () => {
      const { data } = await api.get(`/payments/status/${sid}`).catch(() => ({ data: {} }));
      if (data.payment_status === "paid") {
        toast.success("Abbonamento attivato, grazie!");
        setVerifica(false); setSp({}, { replace: true }); load(); ricarica();
      } else if (++n < 15) setTimeout(poll, 2000);
      else { setVerifica(false); toast.error("Pagamento non ancora confermato: ricarica la pagina tra poco"); }
    };
    poll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const abbonati = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/abbonamento/checkout", { origin_url: window.location.origin, piano });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatError(e));
      setBusy(false);
    }
  };

  const abbonatiPayPal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/abbonamento/paypal", { origin_url: window.location.origin, piano });
      window.location.href = data.approve_url;
    } catch (e) {
      toast.error(formatError(e));
      setBusy(false);
    }
  };

  if (!a) return <p className="py-20 text-center text-sm text-slate-500">Caricamento...</p>;
  const attivo = ["attivo", "attivo_gratuito"].includes(a.stato);
  return (
    <div className="max-w-2xl">
      <PageHeader overline={a.nome} title="Abbonamento" />
      <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm" data-testid="abbonamento-card">
        <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Stato</p>
        <p className="mt-1 flex items-center gap-2 font-heading text-xl font-bold" data-testid="abbonamento-stato">
          {attivo && <CheckCircle2 className="h-5 w-5 text-emerald-600" />}{STATI[a.stato] || a.stato}
        </p>
        {a.stato === "prova" && <p className="mt-2 text-sm text-slate-600" data-testid="abbonamento-prova">Prova gratuita fino al <b>{dataIt(a.trial_ends_at?.slice(0, 10))}</b> ({a.giorni_prova} giorni rimasti).</p>}
        {!a.attivo && <p className="mt-2 rounded-md bg-rose-50 p-3 text-sm text-rose-700" data-testid="abbonamento-bloccato">Il periodo di prova è terminato: attiva l'abbonamento per continuare a usare il gestionale. I tuoi dati sono al sicuro.</p>}
        {verifica && <p className="mt-3 flex items-center gap-2 text-sm text-slate-600" data-testid="abbonamento-verifica"><Loader2 className="h-4 w-4 animate-spin" /> Verifica del pagamento in corso...</p>}
        {!attivo && !verifica && (
          <div className="mt-6 border-t border-slate-100 pt-6">
            <div className="mb-5 grid gap-3 sm:grid-cols-2" data-testid="abbonamento-piani">
              <Piano id="mensile" sel={piano} onSel={setPiano} titolo="Mensile" prezzo={`${a.prezzo.toFixed(0)} €`} per="/ mese" nota="Disdici quando vuoi" />
              <Piano id="annuale" sel={piano} onSel={setPiano} titolo="Annuale" prezzo={`${a.prezzo_annuale.toFixed(0)} €`} per="/ anno" nota={`Risparmi ${(a.prezzo * 12 - a.prezzo_annuale).toFixed(0)} € rispetto al mensile`} badge="Conviene" />
            </div>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div><p className="font-mono text-3xl font-bold" data-testid="abbonamento-prezzo">{(piano === "annuale" ? a.prezzo_annuale : a.prezzo).toFixed(0)} €<span className="text-base font-normal text-slate-500"> {piano === "annuale" ? "/ anno" : "/ mese"}</span></p><p className="text-xs text-slate-500">Tutte le funzioni, utenti illimitati.</p></div>
            {user?.role === "admin"
              ? <div className="flex flex-wrap gap-2">
                  <Button onClick={abbonati} disabled={busy} className="bg-orange-600 hover:bg-orange-700" data-testid="abbonamento-checkout-button"><CreditCard className="mr-1 h-4 w-4" /> {busy ? "Apertura..." : "Paga con carta"}</Button>
                  <Button onClick={abbonatiPayPal} disabled={busy} className="bg-[#FFC439] font-bold text-[#003087] hover:bg-[#f2b92f]" data-testid="abbonamento-paypal-button">{busy ? "Apertura..." : <span>Paga con <i>PayPal</i></span>}</Button>
                </div>
              : <p className="text-sm text-slate-500">Chiedi all'amministratore del negozio di attivare l'abbonamento.</p>}
          </div>
          </div>
        )}
      </div>
    </div>
  );
}
